import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EINVOICE_PROFILE_LABELS,
  EINVOICE_PROFILES,
  FINANCE_HINTS,
  HANDOVER_LABELS,
  HANDOVERS,
  LEDGER_FRAMEWORK_LABELS,
  LEDGER_FRAMEWORKS,
  MONTH_LABELS,
  TAX_CODES,
  TAX_MODE_HINTS,
  TAX_MODE_LABELS,
  TAX_MODES,
  type BankAccount,
  type EInvoiceProfile,
  type FinanceSettings,
  type Handover,
  type LedgerFramework,
  type TaxMode,
} from '@kontor/shared';
import { api, ApiError } from '../../api';
import { InfoHint } from '../../components/InfoHint';
import { bpText, parseBp } from '../../money';
import { useSession } from '../../session';
import './finance.css';

interface Form {
  taxMode: TaxMode;
  ledgerFramework: LedgerFramework;
  accountLength: string;
  fiscalYearStartMonth: string;
  handover: Handover;
  paymentTermsDays: string;
  discountPercent: string;
  discountDays: string;
  footerText: string;
  defaultBankAccountId: string;
  einvoiceProfile: EInvoiceProfile;
  retentionYears: string;
  purchaseFourEyes: boolean;
  accounts: Record<string, { revenue: string; tax: string }>;
}

const toForm = (s: FinanceSettings): Form => ({
  taxMode: s.taxMode,
  ledgerFramework: s.ledgerFramework,
  accountLength: String(s.accountLength),
  fiscalYearStartMonth: String(s.fiscalYearStartMonth),
  handover: s.handover,
  paymentTermsDays: String(s.paymentTermsDays),
  discountPercent: s.discountBp ? bpText(s.discountBp) : '',
  discountDays: s.discountDays === null ? '' : String(s.discountDays),
  footerText: s.footerText ?? '',
  defaultBankAccountId: s.defaultBankAccountId ?? '',
  einvoiceProfile: s.einvoiceProfile,
  retentionYears: String(s.retentionYears),
  purchaseFourEyes: s.purchaseFourEyes,
  accounts: Object.fromEntries(TAX_CODES.map((c) => [c.code, { revenue: s.taxAccounts.find((a) => a.code === c.code)?.revenueAccount ?? '', tax: s.taxAccounts.find((a) => a.code === c.code)?.taxAccount ?? '' }])),
});

const whole = (text: string): number => (text.trim() === '' ? Number.NaN : Number(text.trim()));

/**
 * Einrichtung → Finanzen (KON-139): Versteuerungsart, Kontenrahmen und DATEV, Zahlungsbedingungen und Skonto, Konten je Steuerschlüssel, E-Rechnung, Bankkonten.
 * An jeder Auswahl steht ein Hinweistext. Beraternummer und DATEV-Mandantennummer gehören zu den Firmendaten und stehen dort. Die Rechte prüft der Server.
 */
export function FinanceSettingsPage() {
  const { can, hasModule } = useSession();
  const canWrite = can('setup.write');
  const client = useQueryClient();
  const settings = useQuery({ queryKey: ['finance-settings'], queryFn: async () => (await api<{ data: FinanceSettings }>('GET', '/finance/settings')).data });
  const banks = useQuery({ queryKey: ['finance-banks'], queryFn: async () => (await api<{ data: BankAccount[] }>('GET', '/finance/bank-accounts')).data });

  const [form, setForm] = useState<Form | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current || !settings.data) return;
    loaded.current = true;
    setForm(toForm(settings.data));
  }, [settings.data]);

  const save = useMutation({
    mutationFn: async (f: Form) => {
      const percent = f.discountPercent.trim() === '' ? null : parseBp(f.discountPercent);
      if (f.discountPercent.trim() !== '' && percent === null) throw new ApiError(422, 'validation_failed', 'Bitte prüfen Sie Ihre Eingaben.', { discountBp: 'Bitte eine Zahl angeben, zum Beispiel 2 oder 1,5' });
      const body = {
        taxMode: f.taxMode,
        ledgerFramework: f.ledgerFramework,
        accountLength: whole(f.accountLength),
        fiscalYearStartMonth: whole(f.fiscalYearStartMonth),
        handover: f.handover,
        paymentTermsDays: whole(f.paymentTermsDays),
        discountBp: percent,
        discountDays: f.discountDays.trim() === '' ? null : whole(f.discountDays),
        footerText: f.footerText.trim() || null,
        defaultBankAccountId: f.defaultBankAccountId || null,
        einvoiceProfile: f.einvoiceProfile,
        retentionYears: whole(f.retentionYears),
        purchaseFourEyes: f.purchaseFourEyes,
        taxAccounts: TAX_CODES.map((c) => ({ code: c.code, revenueAccount: f.accounts[c.code]!.revenue.trim() || null, taxAccount: f.accounts[c.code]!.tax.trim() || null })),
      };
      return (await api<{ data: FinanceSettings }>('PUT', '/finance/settings', body)).data;
    },
    onSuccess: (s) => {
      client.setQueryData(['finance-settings'], s);
      // Die Versteuerungsart steht auch in den Firmendaten und in Angeboten
      client.invalidateQueries({ queryKey: ['company-profile'] });
      client.invalidateQueries({ queryKey: ['offer-defaults'] });
      setErrors({});
      setNotice({ ok: true, text: 'Die Einstellungen sind gespeichert.' });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setNotice({ ok: false, text: 'Bitte prüfen Sie die markierten Felder.' });
      } else setNotice({ ok: false, text: e instanceof ApiError ? e.message : 'Die Einstellungen konnten nicht gespeichert werden.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  if (settings.isLoading || !form) return <section className="card"><p className="muted">Wird geladen …</p></section>;
  if (settings.isError) return <section className="card"><div className="alert alert--error">{(settings.error as Error).message}</div></section>;

  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm({ ...form, [key]: value });
  const err = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);
  const ownBanks = (banks.data ?? []).filter((b) => b.isOwn && b.isActive);

  function submit(event: FormEvent) {
    event.preventDefault();
    setNotice(null);
    save.mutate(form!);
  }

  return (
    <>
      <form className="card finance-form" onSubmit={submit} noValidate>
        <h2>Finanzen</h2>
        <p className="muted small">
          Diese Einstellungen gelten für Rechnungen, Zahlungen und den Export an Ihren Steuerberater. Beraternummer und DATEV-Mandantennummer tragen Sie in den{' '}
          <Link to="/einrichtung/firmendaten">Firmendaten</Link> ein. Wenn Sie etwas nicht wissen, fragen Sie Ihren Steuerberater, die Hinweise (i) erklären jede Auswahl.
        </p>
        {notice && <div className={`alert ${notice.ok ? 'alert--ok' : 'alert--error'}`} role="status">{notice.text}</div>}

        <fieldset className="field-card" disabled={!canWrite}>
          <legend>Versteuerung</legend>
          <div className="finance-radios" role="radiogroup" aria-label="Versteuerungsart">
            {TAX_MODES.map((m) => (
              <label key={m} className={`finance-radio${form.taxMode === m ? ' is-selected' : ''}`}>
                <input type="radio" name="taxMode" value={m} checked={form.taxMode === m} onChange={() => set('taxMode', m)} />
                <span>
                  {TAX_MODE_LABELS[m]}
                  <InfoHint label={TAX_MODE_LABELS[m]}>{TAX_MODE_HINTS[m]}</InfoHint>
                </span>
              </label>
            ))}
          </div>
          <p className="muted small finance-explain">{TAX_MODE_HINTS[form.taxMode]}</p>
          {form.taxMode === 'small_business' && <p className="muted small">Der Schalter „Kleinunternehmer“ in den Firmendaten ist damit ebenfalls gesetzt.</p>}
        </fieldset>

        <fieldset className="field-card" disabled={!canWrite}>
          <legend>Kontenrahmen und DATEV</legend>
          <div className="form-grid">
            <label className="field">
              <span>Kontenrahmen<InfoHint label="Kontenrahmen">{FINANCE_HINTS.ledgerFramework}</InfoHint></span>
              <select className="input" value={form.ledgerFramework} onChange={(e) => set('ledgerFramework', e.target.value as LedgerFramework)} disabled={hasModule('chart')}>
                {LEDGER_FRAMEWORKS.map((f) => <option key={f} value={f}>{LEDGER_FRAMEWORK_LABELS[f]}</option>)}
              </select>
              {hasModule('chart') && <small className="muted">Den Kontenrahmen wechseln Sie in Einrichtung → <Link to="/einrichtung/kontenplan">Kontenplan</Link>, dort passen sich auch die Konten an.</small>}
            </label>
            <label className="field">
              <span>Länge der Sachkonten<InfoHint label="Länge der Sachkonten">{FINANCE_HINTS.accountLength}</InfoHint></span>
              <input className="input num" inputMode="numeric" value={form.accountLength} onChange={(e) => set('accountLength', e.target.value)} aria-invalid={!!errors.accountLength} />
              {err('accountLength')}
            </label>
            <label className="field">
              <span>Beginn des Wirtschaftsjahres<InfoHint label="Beginn des Wirtschaftsjahres">{FINANCE_HINTS.fiscalYearStart}</InfoHint></span>
              <select className="input" value={form.fiscalYearStartMonth} onChange={(e) => set('fiscalYearStartMonth', e.target.value)}>
                {MONTH_LABELS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Übergabe an den Steuerberater<InfoHint label="Übergabe an den Steuerberater">{FINANCE_HINTS.handover}</InfoHint></span>
              <select className="input" value={form.handover} onChange={(e) => set('handover', e.target.value as Handover)}>
                {HANDOVERS.map((h) => <option key={h} value={h}>{HANDOVER_LABELS[h]}</option>)}
              </select>
            </label>
          </div>
        </fieldset>

        <fieldset className="field-card" disabled={!canWrite}>
          <legend>Zahlungsbedingungen</legend>
          <div className="form-grid">
            <label className="field">
              <span>Zahlungsziel in Tagen<InfoHint label="Zahlungsziel">{FINANCE_HINTS.paymentTerms}</InfoHint></span>
              <input className="input num" inputMode="numeric" value={form.paymentTermsDays} onChange={(e) => set('paymentTermsDays', e.target.value)} aria-invalid={!!errors.paymentTermsDays} />
              {err('paymentTermsDays')}
            </label>
            <label className="field">
              <span>Skonto in Prozent<InfoHint label="Skonto">{FINANCE_HINTS.discount}</InfoHint></span>
              <input className="input num" inputMode="decimal" placeholder="zum Beispiel 2" value={form.discountPercent} onChange={(e) => set('discountPercent', e.target.value)} aria-invalid={!!errors.discountBp} />
              {err('discountBp')}
            </label>
            <label className="field">
              <span>Skonto, wenn gezahlt innerhalb von (Tagen)</span>
              <input className="input num" inputMode="numeric" placeholder="zum Beispiel 10" value={form.discountDays} onChange={(e) => set('discountDays', e.target.value)} aria-invalid={!!errors.discountDays} />
              {err('discountDays')}
            </label>
          </div>
          <p className="muted small">Das ist die Vorgabe. Sie lässt sich je Kunde und in jedem Angebot, Auftrag und jeder Rechnung ändern.</p>
        </fieldset>

        <fieldset className="field-card" disabled={!canWrite}>
          <legend>Rechnungen und Belege</legend>
          <div className="form-grid">
            <label className="field">
              <span>Standard-Bankkonto</span>
              <select className="input" value={form.defaultBankAccountId} onChange={(e) => set('defaultBankAccountId', e.target.value)} aria-invalid={!!errors.defaultBankAccountId}>
                <option value="">Keines (Bankverbindung aus den Firmendaten)</option>
                {ownBanks.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.iban})</option>)}
              </select>
              {err('defaultBankAccountId')}
            </label>
            <label className="field">
              <span>Format der E-Rechnung<InfoHint label="Format der E-Rechnung">{FINANCE_HINTS.einvoiceProfile}</InfoHint></span>
              <select className="input" value={form.einvoiceProfile} onChange={(e) => set('einvoiceProfile', e.target.value as EInvoiceProfile)}>
                {EINVOICE_PROFILES.map((p) => <option key={p} value={p}>{EINVOICE_PROFILE_LABELS[p]}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Aufbewahrung in Jahren<InfoHint label="Aufbewahrung">{FINANCE_HINTS.retention}</InfoHint></span>
              <input className="input num" inputMode="numeric" value={form.retentionYears} onChange={(e) => set('retentionYears', e.target.value)} aria-invalid={!!errors.retentionYears} />
              {err('retentionYears')}
            </label>
          </div>
          <label className="field">
            <span>Standard-Fußtext auf Rechnungen</span>
            <textarea className="input textarea" rows={3} maxLength={1000} value={form.footerText} onChange={(e) => set('footerText', e.target.value)} placeholder="zum Beispiel: Vielen Dank für Ihren Auftrag." />
          </label>
          <label className="field field--check">
            <input type="checkbox" checked={form.purchaseFourEyes} onChange={(e) => set('purchaseFourEyes', e.target.checked)} />
            <span>Vier-Augen-Prinzip bei Eingangsrechnungen<InfoHint label="Vier-Augen-Prinzip">Wer eine Eingangsrechnung erfasst, darf sie nicht selbst freigeben. Eine zweite Person prüft und gibt frei. Das schützt vor Fehlern und vor Missbrauch und ist bei mehreren Mitarbeitern sinnvoll.</InfoHint></span>
          </label>
          <p className="muted small">Barzahlungen tragen Sie bei der Rechnung als Zahlung ein. {FINANCE_HINTS.cash}</p>
        </fieldset>

        <fieldset className="field-card" disabled={!canWrite}>
          <legend>Konten je Steuerschlüssel<InfoHint label="Konten je Steuerschlüssel">{FINANCE_HINTS.accounts}</InfoHint></legend>
          <div className="finance-accounts">
            <table className="table table--cards">
              <thead>
                <tr>
                  <th>Steuerschlüssel</th>
                  <th>Erlöskonto</th>
                  <th>Steuerkonto</th>
                </tr>
              </thead>
              <tbody>
                {TAX_CODES.map((c) => (
                  <tr key={c.code}>
                    <td>
                      <strong>{c.code}</strong> <span className="muted small">{c.label}</span>
                    </td>
                    <td data-label="Erlöskonto">
                      <input className="input num" inputMode="numeric" aria-label={`Erlöskonto ${c.code}`} maxLength={8} value={form.accounts[c.code]!.revenue} onChange={(e) => setForm({ ...form, accounts: { ...form.accounts, [c.code]: { ...form.accounts[c.code]!, revenue: e.target.value } } })} aria-invalid={!!errors.taxAccounts} />
                    </td>
                    <td data-label="Steuerkonto">
                      {c.rate > 0 ? (
                        <input className="input num" inputMode="numeric" aria-label={`Steuerkonto ${c.code}`} maxLength={8} value={form.accounts[c.code]!.tax} onChange={(e) => setForm({ ...form, accounts: { ...form.accounts, [c.code]: { ...form.accounts[c.code]!, tax: e.target.value } } })} />
                      ) : (
                        <span className="muted small">entfällt</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {err('taxAccounts')}
        </fieldset>

        {canWrite && (
          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>
          </div>
        )}
      </form>

      <BankAccounts canWrite={canWrite} />
    </>
  );
}

/** Bankkonten: eigene für Rechnungen und Zahlungseingänge, fremde für Zahlungsausgänge. Konten lassen sich nur deaktivieren. */
function BankAccounts({ canWrite }: { canWrite: boolean }) {
  const client = useQueryClient();
  const banks = useQuery({ queryKey: ['finance-banks'], queryFn: async () => (await api<{ data: BankAccount[] }>('GET', '/finance/bank-accounts')).data });
  const [name, setName] = useState('');
  const [iban, setIban] = useState('');
  const [bic, setBic] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const refresh = () => {
    client.invalidateQueries({ queryKey: ['finance-banks'] });
    client.invalidateQueries({ queryKey: ['finance-settings'] });
  };
  const create = useMutation({
    mutationFn: () => api('POST', '/finance/bank-accounts', { name: name.trim(), iban, bic: bic.trim() || null, isOwn: true }),
    onSuccess: () => {
      setName('');
      setIban('');
      setBic('');
      setErrors({});
      refresh();
    },
    onError: (e) => setErrors(e instanceof ApiError ? (e.status === 422 ? e.fields : { iban: e.message }) : { iban: 'Das Konto konnte nicht angelegt werden.' }),
  });
  const toggle = useMutation({ mutationFn: (b: BankAccount) => api('PATCH', `/finance/bank-accounts/${b.id}`, { isActive: !b.isActive }), onSuccess: refresh });
  const toggleError = toggle.error instanceof ApiError ? toggle.error.message : null;

  return (
    <section className="card">
      <h2>Bankkonten</h2>
      <p className="muted small">Ihre Konten für Rechnungen und Zahlungen. Ein Konto lässt sich nicht löschen, nur deaktivieren, damit alte Belege es weiter nennen können.</p>
      {toggleError && <div className="alert alert--error" role="alert">{toggleError}</div>}
      <table className="table table--cards">
        <thead>
          <tr>
            <th>Bezeichnung</th>
            <th>IBAN</th>
            <th>BIC</th>
            <th>Stand</th>
            <th><span className="sr-only">Aktionen</span></th>
          </tr>
        </thead>
        <tbody>
          {banks.isLoading && <tr><td colSpan={5} className="muted">Wird geladen …</td></tr>}
          {!banks.isLoading && (banks.data ?? []).length === 0 && <tr><td colSpan={5} className="empty">Noch kein Bankkonto angelegt.</td></tr>}
          {(banks.data ?? []).map((b) => (
            <tr key={b.id} className={b.isActive ? undefined : 'row--muted'}>
              <td><strong>{b.name}</strong></td>
              <td data-label="IBAN">{b.iban.replace(/(.{4})/g, '$1 ').trim()}</td>
              <td data-label="BIC">{b.bic ?? '–'}</td>
              <td data-label="Stand">{b.isActive ? 'Aktiv' : 'Deaktiviert'}</td>
              <td className="cell-actions">
                {canWrite && <button type="button" className="btn btn--secondary btn--sm" disabled={toggle.isPending} onClick={() => toggle.mutate(b)}>{b.isActive ? 'Deaktivieren' : 'Aktivieren'}</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canWrite && (
        <form
          className="finance-bank-form"
          onSubmit={(e) => {
            e.preventDefault();
            setErrors({});
            create.mutate();
          }}
          noValidate
        >
          <label className="field"><span>Bezeichnung</span><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="zum Beispiel Sparkasse" maxLength={120} aria-invalid={!!errors.name} /></label>
          <label className="field">
            <span>IBAN</span>
            <input className="input" value={iban} onChange={(e) => setIban(e.target.value)} placeholder="DE89 3704 0044 0532 0130 00" aria-invalid={!!errors.iban} />
            {errors.iban && <small className="field__error" role="alert">{errors.iban}</small>}
          </label>
          <label className="field">
            <span>BIC (freiwillig)</span>
            <input className="input" value={bic} onChange={(e) => setBic(e.target.value)} placeholder="COBADEFFXXX" maxLength={11} aria-invalid={!!errors.bic} />
            {errors.bic && <small className="field__error" role="alert">{errors.bic}</small>}
          </label>
          <button className="btn btn--primary finance-bank-form__submit" type="submit" disabled={create.isPending || !name.trim() || !iban.trim()}>{create.isPending ? 'Wird angelegt …' : 'Konto anlegen'}</button>
        </form>
      )}
    </section>
  );
}
